import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ResourceCard, ResourceCardLogo } from 'components/blocks/resource-card';
import { configureCardSizeClassName } from 'constant/configure-layout';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { cn } from 'lib/utils';
import { useCommonStore } from 'state/store/common/useCommonStore';
import type { IamData } from 'features/iam/services/user-service';
import type { PromptItem } from '../types';
import { extractVariables, getInitialsFromName, getOwnerDisplay } from '../utils';
import { PromptActionDropdown } from './prompt-action-dropdown';

type PromptRowProps = {
  prompt: PromptItem;
  owner?: IamData;
  onEdit: (prompt: PromptItem) => void;
  showOwner?: boolean;
  /** Folder `ownerId` used for edit/delete permission when provided. */
  ownerId?: string;
};

export function PromptRow({ prompt, owner, onEdit, showOwner = false, ownerId }: PromptRowProps) {
  const { t } = useTranslation();
  const user = useCommonStore((state) => state.user);
  const variables = extractVariables(prompt.body);
  const ownerDisplay = getOwnerDisplay(ownerId ?? prompt.createdBy, owner, user);
  const variableCount = variables.length;
  const variablesLabel =
    t('PROMPT_VARIABLES_COUNT', {
      count: variableCount,
      defaultValue: `${variableCount} variables`,
    }) || `${variableCount} variables`;
  const showOwnerRow = showOwner && Boolean(ownerDisplay.name);

  return (
    <ResourceCard
      title={prompt.title}
      description={prompt.body}
      icon={
        <ResourceCardLogo name={prompt.title}>
          <FileText className="h-5 w-5" />
        </ResourceCardLogo>
      }
      actions={
        <PromptActionDropdown prompt={prompt} ownerId={ownerId} onEdit={() => onEdit(prompt)} />
      }
      className={configureCardSizeClassName}
      footer={
        <div className="flex min-h-7 items-center justify-between gap-2">
          {variableCount > 0 ? (
            <span className="flex min-w-0 flex-1 items-center gap-1.5" title={variablesLabel}>
              <span
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full border',
                  'border-violet-200 bg-violet-50 text-violet-600 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-400'
                )}
              >
                <FileText className="size-3.5" strokeWidth={1.75} />
              </span>
              <span className="min-w-0 truncate text-xs font-normal text-grey-500 dark:text-muted-foreground">
                {variablesLabel}
              </span>
            </span>
          ) : null}
          {showOwnerRow ? (
            <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1.5">
              <Avatar className="h-6 w-6 border border-grey-100 dark:border-border">
                {ownerDisplay.imageUrl ? (
                  <AvatarImage src={ownerDisplay.imageUrl} alt={ownerDisplay.name} />
                ) : null}
                <AvatarFallback className="text-[10px]">
                  {getInitialsFromName(ownerDisplay.name)}
                </AvatarFallback>
              </Avatar>
              <span
                className="truncate text-xs text-grey-500 dark:text-muted-foreground"
                title={ownerDisplay.name}
              >
                {ownerDisplay.name}
              </span>
            </div>
          ) : null}
        </div>
      }
    />
  );
}
